import type { FormEvent } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { ROUTES } from '@shared/config/routes';
import { Button } from '@shared/ui/button';
import { Card, CardContent } from '@shared/ui/card';
import { CortexaLogo } from '@shared/ui/cortexa-logo';
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldSeparator } from '@shared/ui/field';
import { Input } from '@shared/ui/input';
import { Heading } from '@shared/ui/typography';

const preventMockSubmit = (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
};

const GoogleIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
    <path d="M1 1h22v22H1z" fill="none" />
  </svg>
);

const LoginMockup = () => {
  const navigate = useNavigate();

  return (
    <div className="flex w-full flex-col gap-6">
      <Card className="overflow-hidden rounded-2xl py-0 shadow-lg">
        <CardContent>
          <form
            className="
              p-5
              md:p-6
            "
            onSubmit={preventMockSubmit}
          >
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center"></div>
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input className="h-10" id="email" type="email" placeholder="student@cortexa.ai" />
              </Field>
              <Field>
                <div className="flex items-center">
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <span className="ml-auto text-sm text-muted-foreground">Forgot password?</span>
                </div>
                <Input
                  className="h-10"
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                />
              </Field>
              <Field>
                <Button
                  size="xl"
                  type="button"
                  onClick={() => {
                    navigate(ROUTES.DASHBOARD);
                  }}
                >
                  Sign In
                </Button>
              </Field>
              <FieldSeparator
                className="
                  uppercase
                  *:data-[slot=field-separator-content]:bg-card
                "
              >
                Or
              </FieldSeparator>
              <Field>
                <Button
                  aria-label="Continue with Google"
                  variant="outline"
                  type="button"
                  size="lg"
                  onClick={() => {
                    navigate(ROUTES.DASHBOARD);
                  }}
                >
                  <GoogleIcon />
                  Continue with Google
                </Button>
              </Field>
              <FieldDescription className="text-center text-base">
                Already have an account? <Link to={ROUTES.LOGIN}>Sign in</Link>.
              </FieldDescription>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
      <FieldDescription className="px-6 text-center">
        By continuing, you agree to our <Link to={ROUTES.TERMS_PRIVACY}>Terms & Privacy</Link>.
      </FieldDescription>
    </div>
  );
};

export const LoginPage = () => {
  return (
    <div className="flex min-h-dvh w-screen bg-background-2">
      <div className="relative m-auto flex w-full max-w-xl grow flex-col items-center px-6 py-10">
        <CortexaLogo size="lg" icon={true} className="relative mb-8 size-20" />
        <Heading tag="h1" size="5xl">
          Cortexa AI Test Prep
        </Heading>
        <p className="mt-3 mb-8 text-center text-lg/7">
          Adaptive tutoring across every major exam.
        </p>
        <LoginMockup />
      </div>
    </div>
  );
};
